import { MYDRA_WORKSPACE_ID_HEADER_NAME } from '@/config/system'
import { PlaybooksService } from '@/services/playbook'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import {
  ApiErrorResponse,
  ApiIndividualResponse,
  ApiReactMutationOptions,
} from '@/types/api'
import {
  ApiPlaybookCreationResponse,
  ApiPlaybookUpdateRequest,
} from '@/types/playbook'
import { useMutation, useQueryClient } from '@tanstack/react-query'

type UpdatePlaybookParams = {
  id: string
  playbookData: ApiPlaybookUpdateRequest
}

export const useUpdatePlaybook = (
  mutationOptions?: ApiReactMutationOptions<
    ApiIndividualResponse<ApiPlaybookCreationResponse>,
    UpdatePlaybookParams
  >
) => {
  const queryClient = useQueryClient()
  const { workspaceId } = useWorkspaceContext()

  return useMutation<
    ApiIndividualResponse<ApiPlaybookCreationResponse>,
    ApiErrorResponse,
    UpdatePlaybookParams
  >({
    ...mutationOptions,
    mutationKey: [
      'playbooks',
      'update',
      ...(workspaceId ? ['workspaceId', workspaceId] : []),
    ],
    mutationFn: ({ id, playbookData }: UpdatePlaybookParams) =>
      PlaybooksService.updatePlaybook(id, playbookData, {
        headers: {
          [MYDRA_WORKSPACE_ID_HEADER_NAME]: workspaceId,
        },
      }),
    onSuccess: async (...args) => {
      queryClient.invalidateQueries({
        queryKey: ['playbooks'],
        exact: false,
      })
      await mutationOptions?.onSuccess?.(...args)
    },
  })
}
